/*
axiosClient — central axios instance.

KAN-39 interceptor + axios-retry ordering
-----------------------------------------
Two separate mechanisms attach to the same global `axios` in this file:

  (1) axios-retry: installed via `axiosRetry(axios, …)` below. It wraps
      `axios.request` to schedule retries based on its `retryCondition`.
  (2) Our own request / response interceptors (added further down)
      that inject `Authorization: Bearer <jwt>`, refresh on 401, and
      surface the session-expired reason.

Ordering for a request:
  - The request interceptor runs first (axios resolves its interceptor
    chain before dispatching), so we get a chance to proactively
    refresh + stamp the Authorization header.
  - If the response returns an error, axios-retry's wrapper decides
    whether to reschedule; our response-rejected interceptor also
    runs. Critically, the retryCondition below restricts retries to
    `isNetworkOrIdempotentRequestError` (timeouts, ECONNRESET, etc.)
    OR 5xx responses. A 401 is **not** network/idempotent and is not
    >= 500, so axios-retry DOES NOT fire on the paths our refresh
    interceptor handles — there is no double-retry of /api/* on 401.
  - The `__refreshRetried` flag stamped on the config is our own
    single-shot guard independent of axios-retry's counter.

If someone later widens `retryCondition` to include 4xx, collisions
with `__refreshRetried` become possible; please re-evaluate before
merging such a change.

Note: the test build aliases axios-retry to a no-op stub
(see build/webpack.test.conf.js) because is-retry-allowed ships ES6
PhantomJS cannot parse. The prod bundle uses the real lib.
*/

import axios from 'axios'
import axiosRetry from 'axios-retry/dist/cjs/index'

import {
  getAccessToken,
  getRefreshToken,
  getClaims,
  refresh as refreshSession,
  setPostLoginRedirect,
  isLoginInFlight
} from '@/lib/auth'
import { JWT_PROACTIVE_REFRESH_SECONDS } from '@/lib/env'
import { toAppDateTimeSql, isDateLike } from '@/lib/appTime'
import {
  endSession,
  registerResetHook,
  _resetSessionForTests,
  LOGOUT_REASON_KEY as SESSION_LOGOUT_REASON_KEY
} from '@/lib/session'

// XOL-144: cross-origin cookie support for XOL-145's xolas_auth session
// cookie. Without this, the browser's "omit credentials" default discards
// every Set-Cookie on the response and drops cookies from outgoing requests.
// Paired with API CORS supports_credentials=true and SameSite=None;Secure
// on the cookie in dev (cross-origin :8080↔:8000); SameSite=Lax in prod
// (same-origin SPA + API).
axios.defaults.withCredentials = true

/* XOL-414: never let a Date reach the wire as UTC.
------------------------------------------------------------------------------
The API reads every datetime as bare wall clock in ITS configured zone — sent
to us on `api/system/config` since XOL-449, not assumed to be +08:00. axios' stock
transform is `JSON.stringify`, which calls `Date.prototype.toJSON()` and emits
`2026-08-16T16:00:00.000Z` — and moment's `toJSON` ignores `utcOffset`, so a
KL-pinned moment leaks UTC too.

Most payloads are normalised at the call site with `formatDateSql` /
`formatDateTimeSql`, but a handful are not: `progress/ProgressStep1-4.vue` PUT
`this.step` verbatim with their normalisation block commented out,
`requests/workflow/step4.vue` binds pickers into the free-form `step.state`
blob, `actions/RenewPermitAction.vue` posts its form directly, and
`assets/features/Billable.vue`'s `rentStarted` is in no format list at all.
This is the net under all of them, and under whatever gets added next.

`unshift`, never assign: the stock entry is what sets `Content-Type:
application/json` and stringifies, so replacing the array breaks every POST in
the app. Running first is what lets us see the object rather than a string.
------------------------------------------------------------------------------ */
// A Date nested deeper than this passes through untouched and would reach the
// wire as UTC ISO — i.e. the cap trades the no-Z invariant for a guarantee that
// a leaked component reference cannot hang a request. Nothing in the app nests
// payloads anywhere near this deep.
const DATE_WALK_MAX_DEPTH = 12

// Arrays and plain objects get walked. Everything else — FormData, Blob, File,
// ArrayBuffer(View), URLSearchParams, streams — is passed through untouched;
// rebuilding a FormData as a plain object silently posts an empty body.
function isWalkableContainer (value) {
  if (Array.isArray(value)) {
    return true
  }
  return Object.prototype.toString.call(value) === '[object Object]'
}

function normaliseDatesForWire (value, seen, depth) {
  if (isDateLike(value)) {
    return toAppDateTimeSql(value)
  }

  if (value === null || typeof value !== 'object') {
    return value
  }

  if (depth >= DATE_WALK_MAX_DEPTH || !isWalkableContainer(value)) {
    return value
  }

  // Memoise input -> output rather than just marking the input "visited". A
  // plain visited-set breaks a cycle but also hands back the ORIGINAL object
  // the second time the same container is reached by a different path, Dates
  // intact — e.g. `{step: s, meta: {step: s}}` sent `meta.step.approved_at` as
  // `2026-08-16T16:00:00.000Z` while `step.approved_at` was correct. Returning
  // the memoised converted value fixes the shared reference and still
  // terminates on a cycle, because the entry is written before we recurse.
  if (seen.has(value)) {
    return seen.get(value)
  }

  if (Array.isArray(value)) {
    // Indexed rather than forEach+push: forEach skips holes, so a sparse
    // [a, , c] would rebuild as length 2 where JSON.stringify emits [a,null,c].
    const arr = new Array(value.length)
    seen.set(value, arr)
    for (var i = 0; i < value.length; i++) {
      arr[i] = normaliseDatesForWire(value[i], seen, depth + 1)
    }
    return arr
  }

  // New object every time — never mutate the caller's reactive form data.
  // Object.keys matches what JSON.stringify would have seen (Vue 2's `__ob__`
  // is non-enumerable).
  const out = {}
  seen.set(value, out)
  Object.keys(value).forEach(function (key) {
    out[key] = normaliseDatesForWire(value[key], seen, depth + 1)
  })
  return out
}

axios.defaults.transformRequest.unshift(function xolasNormaliseDates (data) {
  // Strings arrive here on the second pass: dispatchRequest mutates
  // config.data in place, so an axios-retry replay or the token-refresh
  // re-dispatch below re-runs the chain on an already-stringified body.
  if (data === null || data === undefined || typeof data !== 'object') {
    return data
  }

  if (isDateLike(data)) {
    return toAppDateTimeSql(data)
  }

  if (!isWalkableContainer(data)) {
    return data
  }

  return normaliseDatesForWire(data, new WeakMap(), 0)
})

// Configure axios-retry to retry 3 times on failure
axiosRetry(axios, {
  retries: 3,
  // Exponential backoff: 100ms, 200ms, 400ms, 800ms, 1600ms
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    // Retry only on network errors or 5xx server errors.
    // IMPORTANT: 401 must NOT land here — the refresh+retry dance lives
    // in the response interceptor below. See the module header for why
    // this ordering is safe.
    return axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      (error.response && error.response.status >= 500)
  }
})

// Function to generate a unique ID
function generateRequestId () {
  // Use crypto.randomUUID() for a standard UUID (Node.js/modern browsers)
  // or a simple alternative if needed
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  } else {
    // Fallback for older environments
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
  }
}

const REFRESH_PATH = '/api/auth/refresh'
const LOGIN_PATH = '/api/login'

// Strip the query string + hash so the suffix check below is unambiguous.
function pathnameOf (url) {
  if (!url) {
    return ''
  }
  let out = String(url)
  const hashIdx = out.indexOf('#')
  if (hashIdx !== -1) {
    out = out.slice(0, hashIdx)
  }
  const queryIdx = out.indexOf('?')
  if (queryIdx !== -1) {
    out = out.slice(0, queryIdx)
  }
  return out
}

// Explicit suffix check: PhantomJS 2 (our karma browser) does NOT ship
// String.prototype.endsWith, so avoid it in the prod bundle.
function endsWithStr (haystack, needle) {
  if (!haystack || !needle) {
    return false
  }
  if (needle.length > haystack.length) {
    return false
  }
  return haystack.lastIndexOf(needle) === (haystack.length - needle.length)
}

// Exact-suffix match on pathname — substring matches falsely exempted
// sibling endpoints such as `/api/auth/refreshPassword` or
// `/api/loginHistory`, which would have skipped the Authorization
// header in prod.
function isAuthExemptUrl (url) {
  if (!url) {
    return false
  }
  const path = pathnameOf(url)
  if (!path) {
    return false
  }
  return (
    path === REFRESH_PATH ||
    endsWithStr(path, REFRESH_PATH) ||
    path === LOGIN_PATH ||
    endsWithStr(path, LOGIN_PATH)
  )
}

// -- Single-flight refresh ----------------------------------------------------
// Concurrent proactive/reactive callers share one in-flight promise.
let inFlightRefresh = null

function singleFlightRefresh () {
  if (inFlightRefresh) {
    return inFlightRefresh
  }
  inFlightRefresh = refreshSession()
    .then(function (result) {
      inFlightRefresh = null
      return result
    }, function (err) {
      inFlightRefresh = null
      throw err
    })
  return inFlightRefresh
}

function shouldProactivelyRefresh () {
  const claims = getClaims()
  if (!claims || !claims.exp) {
    return false
  }
  const nowSec = Math.floor(Date.now() / 1000)
  const remaining = Number(claims.exp) - nowSec
  return remaining > 0 && remaining < Number(JWT_PROACTIVE_REFRESH_SECONDS)
}

// XOL-446: re-exported from lib/session.js, which owns every storage key and
// is responsible for preserving this one across a teardown.
export const LOGOUT_REASON_KEY = SESSION_LOGOUT_REASON_KEY
export const LOGOUT_REASON_SESSION_EXPIRED = 'session-expired'
export const LOGOUT_REASON_API_ACCESS_DENIED = 'api-access-denied'

// Auth-failure body code emitted by the API's auth-layer middleware.
// The API also emits `X-Auth-Failure: <code>` on the wire, but CORS
// `exposed_headers` is empty on the API side (XOL-192), so the
// header is filtered before axios can read it. We read the response body
// instead. XOL-192 tracks the API-side CORS change; once that ships we
// can switch back to header-based detection (more semantically clean —
// body shape isn't a stable contract). For now, read the body.
const AUTH_FAILURE_CLIENT_GATE = 'client_gate'

// Tracks whether the most recent forced-logout was a client_gate reject
// (vs. a session-expired). The reactive-retry error handler reads this
// to avoid overwriting an 'api-access-denied' flash with 'session-expired'
// when a single-flight refresh itself gate-rejects mid-flight.
let lastForcedLogoutWasGate = false

// Detects the auth-failure family marker from the response body's `error`
// field. The API also emits `X-Auth-Failure: <code>` on the wire (see
// XolasClientGate / XolasJwtAuth etc.), but CORS exposed_headers is empty
// on the API side as of XOL-191, so the header is filtered before
// axios can read it. XOL-192 tracks the API-side CORS change; once that
// ships we can switch back to header-based detection (more semantically
// clean — body shape isn't a stable contract). For now, read the body.
function getAuthFailureCode (error) {
  if (!error || !error.response || !error.response.data) return null
  const code = error.response.data.error
  return typeof code === 'string' ? code : null
}

function redirectToLogin (reason) {
  // XOL-370: a login is currently in flight (canLogin's post-/api/login
  // chain — updateConfig()/refreshStats() — hasn't finished navigating
  // yet). A racing bootstrap 401 in that window must not clobber the
  // just-issued tokens or bounce the user back to /login with a
  // session-expired flash. Skip every forced-logout side effect below and
  // let the in-flight login finish; if that login's own network calls are
  // themselves failing terminally, canLogin's .catch clears the flag and
  // the user sees the ordinary invalid-credentials/login-failed message
  // instead of a phantom "session expired" banner.
  var loginInFlight = isLoginInFlight()
  if (loginInFlight) {
    return
  }
  // A "session expired" flash should only appear when the browser actually
  // had tokens going in. On boot, schema/config probes 401 with no JWT
  // present — that's "not signed in yet", not "just got signed out" — so
  // we must not persist a reason for that case. Capture the signal before
  // clearing, then clear, then set the flash only if a session existed.
  const hadSession = !!getAccessToken() || !!getRefreshToken()
  // Save the current in-app route so the user lands there after re-login.
  // Only save when a session actually existed (avoids storing redirect for
  // unauthenticated probes). sanitizeRedirectTarget (called inside
  // setPostLoginRedirect) will reject /login, /logout, and empty strings.
  if (hadSession && typeof window !== 'undefined' && window.location) {
    try {
      var hash = window.location.hash || ''
      // hash is "#/some/path" — strip the leading "#"
      var hashPath = hash.charAt(0) === '#' ? hash.slice(1) : hash
      if (hashPath) {
        setPostLoginRedirect(hashPath)
      }
    } catch (e) {
      // ignore
    }
  }
  /*
  XOL-446: full teardown, and the reload that actually happens.

  This used to be `clearTokens()` + `setLogoutReason()` +
  `window.location.assign('/#/login')`. Two problems:

    1. It cleared the JWTs and nothing else — localStorage['xolas-user'] and
       sessionStorage['xolas-config'] survived, along with every module-scope
       cache. This is the path a DB swap actually travels (the stale JWT still
       decodes, so the first authenticated XHR is what 401s), so it is where
       the stale-session bug lived.
    2. `assign('/#/login')` from pathname '/' differs from the current URL
       only in the fragment, which is a fragment navigation — no document
       unload, no reload. It looked like a hard navigation and was not.

  endSession() handles the storage/cookie/in-memory teardown, then relocates
  and reloads for real. `hard` is gated on hadSession so an unauthenticated
  bootstrap probe cannot trigger a reload, and endSession additionally holds
  a 10s fuse so a persistently-401ing boot cannot loop.

  `callApi: false` — we are inside a response interceptor and the tokens are
  already known-bad, so there is nothing worth POSTing to /api/logout with.
  The reason is passed through rather than written here so it is captured
  before the sweep and restored after it.
  */
  endSession({
    reason: (reason && hadSession) ? reason : null,
    hard: hadSession,
    callApi: false
  })
}

// -- Request interceptor ------------------------------------------------------
axios.interceptors.request.use(
  (config) => {
    // Per-request correlation ID
    if (!config.headers) {
      config.headers = {}
    }
    config.headers['X-Request-Id'] = generateRequestId()

    const url = config.url || ''
    if (isAuthExemptUrl(url)) {
      // /api/login & /api/auth/refresh: never inject Authorization, never refresh.
      return config
    }

    // Proactive refresh: if access exp - now < threshold AND a refresh token
    // is present, await the single-flight refresh before sending.
    const refreshToken = getRefreshToken()
    if (refreshToken && shouldProactivelyRefresh()) {
      return singleFlightRefresh()
        .then(function () {
          const access = getAccessToken()
          if (access) {
            config.headers['Authorization'] = 'Bearer ' + access
          }
          return config
        }, function (err) {
          // Refresh failed: send the request with the (likely-expired) token
          // so the response interceptor can handle the 401 path consistently.
          // Logged so ops has a breadcrumb when proactive refresh degrades.
          try { console.warn('proactive refresh failed', err) } catch (e) { /* ignore */ }
          const access = getAccessToken()
          if (access) {
            config.headers['Authorization'] = 'Bearer ' + access
          }
          return config
        })
    }

    const access = getAccessToken()
    if (access) {
      config.headers['Authorization'] = 'Bearer ' + access
    }
    return config
  },
  (error) => Promise.reject(error)
)

// -- Response interceptor -----------------------------------------------------
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error && error.config ? error.config : null
    const status = error && error.response ? error.response.status : null

    if (!config || status !== 401) {
      return Promise.reject(error)
    }

    // NEW: client_gate 401 — the SPA's X-API-Key is not accepted by the API.
    // Detected via the response body `error` field (body-based detection —
    // see getAuthFailureCode comment and XOL-192 for the future switch to
    // the cleaner header-based path). Refresh-then-retry won't help (refresh
    // hits the same gate), and the user can't fix this by re-entering
    // credentials. Clear tokens and surface a distinct flash on /login.
    if (getAuthFailureCode(error) === AUTH_FAILURE_CLIENT_GATE) {
      lastForcedLogoutWasGate = true
      redirectToLogin(LOGOUT_REASON_API_ACCESS_DENIED)
      return Promise.reject(error)
    }

    const url = config.url || ''
    if (isAuthExemptUrl(url)) {
      // 401 on /api/login or /api/auth/refresh => terminal. Don't recurse.
      const path = pathnameOf(url)
      if (path === REFRESH_PATH || endsWithStr(path, REFRESH_PATH)) {
        redirectToLogin(LOGOUT_REASON_SESSION_EXPIRED)
      }
      return Promise.reject(error)
    }

    if (config.__refreshRetried) {
      // Already retried once after a successful refresh. Terminal.
      redirectToLogin(LOGOUT_REASON_SESSION_EXPIRED)
      return Promise.reject(error)
    }

    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      redirectToLogin(LOGOUT_REASON_SESSION_EXPIRED)
      return Promise.reject(error)
    }

    config.__refreshRetried = true

    return singleFlightRefresh().then(function () {
      const access = getAccessToken()
      if (access) {
        if (!config.headers) {
          config.headers = {}
        }
        config.headers['Authorization'] = 'Bearer ' + access
      }
      return axios(config)
    }, function () {
      // Race guard: if the single-flight refresh itself hit a client_gate 401
      // and we already wrote the api-access-denied flash above, don't
      // overwrite it with session-expired.
      if (!lastForcedLogoutWasGate) {
        redirectToLogin(LOGOUT_REASON_SESSION_EXPIRED)
      }
      return Promise.reject(error)
    })
  }
)

// -- GET de-duplication (preserved from previous implementation) -------------
const originalGet = axios.get

function generateRequestKey (url, config) {
  const keyData = { url }
  if (config && config.params) {
    keyData.params = config.params
  }
  return JSON.stringify(keyData)
}

// XOL-446: entries are `delete`d rather than set to null on settle — the map
// used to grow monotonically for the life of the page. The read guard below
// is a truthiness check, so the semantics are unchanged.
const pendingRequests = {}
axios.get = function (url, config) {
  const requestId = generateRequestKey(url, config)

  if (pendingRequests[requestId]) {
    console.log('request queued:', requestId)
    return new Promise(pendingRequests[requestId].resolver)
  }

  const requests = []
  const resolver = (resolve, reject) => {
    requests.push({ resolve, reject })
  }
  const res = originalGet(url, config)
  pendingRequests[requestId] = {
    res,
    requests,
    resolver
  }

  res.then((data) => {
    requests.forEach((request) => request.resolve(data))
    delete pendingRequests[requestId]
  })
  res.catch((err) => {
    requests.forEach((request) => request.reject(err))
    delete pendingRequests[requestId]
  })

  return new Promise(pendingRequests[requestId].resolver)
}

/*
XOL-446: this module's in-memory caches must not outlive a session.

`lastForcedLogoutWasGate` is the reason the teardown uses a registration
hook rather than a central list of things to clear: it shipped with no reset
at all, so a single `client_gate` 401 permanently suppressed the
session-expired banner for the rest of the page's life. Registering the
reset here, beside the state it resets, is what stops the next cache from
repeating that.
*/
function resetAxiosClientState () {
  inFlightRefresh = null
  lastForcedLogoutWasGate = false
  for (var key in pendingRequests) {
    if (Object.prototype.hasOwnProperty.call(pendingRequests, key)) {
      delete pendingRequests[key]
    }
  }
}

function resetForSpecs () {
  resetAxiosClientState()
  // A hard teardown leaves session.js's tearing-down flag set for the unload
  // that never happens under jsdom; clear it so each spec starts clean.
  // keepHooks, because this module's own hook is registered at import time.
  _resetSessionForTests({ keepHooks: true })
}

registerResetHook('axiosClient', resetAxiosClientState)

// Test-only hook to reset the single-flight promise between specs.
// Gated so the helper does not ship in a production bundle. Webpack's
// DefinePlugin inlines `process.env.NODE_ENV`, letting UglifyJS drop
// the branch entirely during prod minification.
export const _resetRefreshStateForTests = (process.env.NODE_ENV !== 'production')
  ? resetForSpecs
  : undefined

export default axios
